/**
 * Category page content: one map keyed by category id (URL slug, e.g. "weight-loss", "womens-health").
 * Data lives in per-category files (weight-loss.ts, womens-health.ts); this module is the single lookup.
 */

import type { CategoryPageData } from './types'
import { medicationsData } from './medications'
import { weightLossData } from './weight-loss'
import { womensHealthData } from './womens-health'

export type {
    CategoryHeaderData,
    CategoryHeaderWithSearchData,
    CategorySearchData,
    CategoryHeaderImage,
    CategoryHeaderAnchor,
    CategoryHeaderCta,
    ContentAccordionData,
    ContentAccordionItem,
    CategoryContentWithBulletListData,
    FAQData,
    FAQDataItem,
    HowItWorksData,
    HowItWorksStepData,
    MultiVideoPlayerData,
    MultiVideoPlayerVideoItem,
    CategoryStatsData,
    CategoryStatsDataStandard,
    CategoryStatsDataLarge,
    CategoryStatsItem,
    CategoryPageData,
    WholeBodyCarouselData,
    WholeBodyCarouselFirstCard,
    WholeBodyCarouselCard,
    TabbedCardSliderData,
    TestimonialUgcData,
    TestimonialUgcCard,
} from './types'

/** Category id (URL slug) → page data. Single source for which component content to show. */
export const CATEGORY_PAGE_DATA: Record<string, CategoryPageData> = {
    medications: medicationsData,
    'weight-loss': weightLossData,
    'womens-health': womensHealthData,
}

function normalizeId(id: string): string {
    if (!id || typeof id !== 'string') return ''
    return id.trim().toLowerCase().replace(/_/g, '-')
}

export function categoryUsesRefresh(categoryId: string): boolean {
    const key = normalizeId(categoryId)
    return !!(key && CATEGORY_PAGE_DATA[key])
}

export function categoryHasCustomHeader(categoryId: string): boolean {
    const key = normalizeId(categoryId)
    const pageData = key ? CATEGORY_PAGE_DATA[key] : undefined
    return !!(pageData?.categoryHeader || pageData?.categoryHeaderWithSearch)
}

/**
 * Get page data by category id (URL slug). Optionally return a single section.
 * @param categoryId - e.g. "weight-loss", "womens-health"
 * @param section - optional key (e.g. "categoryHeader", "contentAccordion")
 */
export function getCategoryPageData(categoryId: string): CategoryPageData | null
export function getCategoryPageData<T extends keyof CategoryPageData>(
    categoryId: string,
    section: T,
): CategoryPageData[T] | null
export function getCategoryPageData<T extends keyof CategoryPageData>(
    categoryId: string,
    section?: T,
): CategoryPageData | CategoryPageData[T] | null {
    const key = normalizeId(categoryId)
    if (!key) return null
    const data = CATEGORY_PAGE_DATA[key]
    if (!data) return null
    if (section) {
        const value = data[section]
        return (value ?? null) as CategoryPageData[T] | null
    }
    return data
}
